import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import { ConfirmModal } from '../components/ConfirmModal'
import { PendingDot } from '../components/PendingChangeIndicator'
import type { PendingVersionResponse, FieldChangeDto, ReviewVersionRequest, ExtraFieldChangeDto, ExtraFieldResolutionDto } from '../api/types'

// Target fields that extra vendor fields can be mapped to
const MAPPABLE_FIELDS = [
  'title', 'procedure_type', 'status_sample', 'status_patient',
  'outcome_notes', 'derived_material', 'place', 'source_sample_label',
  'transfer_method', 'priority', 'performed_on',
  'performing_entity', 'banking_entity', 'location_performed',
  'location_destination', 'transfer_origin', 'transfer_destination'
]

type ModalState =
  | { type: 'none' }
  | { type: 'review'; version: PendingVersionResponse; action: 'accept' | 'reject' | 'merge' }

export function PendingChangesPage() {
  const { apiClient, isAdmin } = useAuth()
  const [versions, setVersions] = useState<PendingVersionResponse[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reviewingVersion, setReviewingVersion] = useState<string | null>(null)
  const [modal, setModal] = useState<ModalState>({ type: 'none' })
  const [reviewNotes, setReviewNotes] = useState('')
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set())
  const [fieldCategories, setFieldCategories] = useState<Record<string, string>>({})
  const [extraFieldResolutions, setExtraFieldResolutions] = useState<Record<string, ExtraFieldResolutionDto>>({})

  // Track reviewing state in a ref to avoid resetting poll interval
  const isReviewingRef = useRef(false)
  isReviewingRef.current = reviewingVersion !== null

  const loadData = useCallback(async () => {
    if (!apiClient) return

    try {
      setLoading(true)
      setError(null)
      const [versionsRes, categoriesRes] = await Promise.all([
        apiClient.getPendingVersions(page, 25),
        apiClient.getFieldCategories(),
      ])
      setVersions(versionsRes.versions)
      setTotalPages(versionsRes.totalPages)
      setTotalCount(versionsRes.totalCount)
      setFieldCategories(categoriesRes)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to load pending changes')
      }
    } finally {
      setLoading(false)
    }
  }, [apiClient, page])

  useEffect(() => {
    loadData()

    // Poll for new pending changes every 30 seconds
    const interval = setInterval(() => {
      // Skip polling while reviewing to avoid UI disruption
      if (!isReviewingRef.current) {
        loadData()
      }
    }, 30000)

    return () => clearInterval(interval)
  }, [loadData])

  // Check if this is a vendor_proposed entry (empty GUID means no formal version)
  const isVendorProposed = (version: PendingVersionResponse) =>
    version.versionId === '00000000-0000-0000-0000-000000000000' || version.versionNumber === 0

  const handleReview = async (version: PendingVersionResponse, action: 'accept' | 'reject' | 'merge') => {
    if (!apiClient) return

    try {
      setReviewingVersion(version.versionId)
      setError(null)

      if (isVendorProposed(version)) {
        // Use reviewProposed API for vendor_proposed entries
        await apiClient.reviewProposed(version.sampleId, { action: action === 'merge' ? 'accept' : action })
      } else {
        // Use reviewVersion API for formal pending versions
        const request: ReviewVersionRequest = {
          action,
          reviewNotes: reviewNotes || undefined,
        }

        // Include extra field resolutions when accepting/merging
        if (action !== 'reject' && version.extraFieldChanges && Object.keys(extraFieldResolutions).length > 0) {
          request.extraFieldResolutions = extraFieldResolutions
        }

        await apiClient.reviewVersion(version.versionId, request)
      }

      setModal({ type: 'none' })
      setReviewNotes('')
      setExtraFieldResolutions({})
      // Notify navigation to update pending count immediately
      window.dispatchEvent(new CustomEvent('pendingChangesUpdated'))
      await loadData()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Review failed: ${err.message}`)
      } else {
        setError('Review failed')
      }
    } finally {
      setReviewingVersion(null)
    }
  }

  const handleExtraFieldResolution = (vendorKey: string, resolution: 'keep' | 'discard' | 'map', mappedTo?: string) => {
    setExtraFieldResolutions(prev => ({
      ...prev,
      [vendorKey]: { resolution, mappedTo }
    }))
  }

  const initExtraFieldResolutions = (extraFields: Record<string, ExtraFieldChangeDto>) => {
    // Initialize all extra fields with default resolution (keep)
    const defaults: Record<string, ExtraFieldResolutionDto> = {}
    for (const key of Object.keys(extraFields)) {
      defaults[key] = { resolution: 'keep' }
    }
    setExtraFieldResolutions(defaults)
  }

  const toggleExpand = (sampleId: string) => {
    setExpandedVersions(prev => {
      const next = new Set(prev)
      if (next.has(sampleId)) {
        next.delete(sampleId)
      } else {
        next.add(sampleId)
      }
      return next
    })
  }

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '(empty)'
    if (typeof value === 'object') return JSON.stringify(value, null, 2)
    return String(value)
  }

  const getCategoryBadge = (fieldName: string) => {
    const category = fieldCategories[fieldName]
    if (!category) return null

    const colors: Record<string, { bg: string; text: string }> = {
      vendor_authoritative: { bg: 'var(--color-info, #3b82f6)', text: '#fff' },
      team_authoritative: { bg: 'var(--color-warning)', text: '#000' },
      additive: { bg: 'var(--color-success)', text: '#fff' },
      standard: { bg: 'var(--color-text-muted)', text: '#fff' },
    }
    const color = colors[category] || colors.standard

    return (
      <span
        className="badge"
        style={{
          backgroundColor: color.bg,
          color: color.text,
          fontSize: 'var(--font-size-xs)',
          marginLeft: 'var(--spacing-xs)',
        }}
      >
        {category.replace('_', ' ')}
      </span>
    )
  }

  const renderChanges = (changes: Record<string, FieldChangeDto>) => {
    const entries = Object.entries(changes)
    if (entries.length === 0) {
      return <p style={{ color: 'var(--color-text-muted)' }}>No changes recorded</p>
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {entries.map(([field, change]) => (
          <div
            key={field}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-md)',
              backgroundColor: 'var(--color-bg-tertiary)',
              borderRadius: 'var(--radius-sm)',
              borderLeft: '3px solid var(--color-primary)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-xs)' }}>
              <span style={{ fontWeight: 600, fontFamily: 'var(--font-family-mono)' }}>{field}</span>
              {getCategoryBadge(field)}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 'var(--spacing-sm)', alignItems: 'start' }}>
              <div style={{
                padding: 'var(--spacing-sm)',
                backgroundColor: 'rgba(220, 38, 38, 0.1)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                minHeight: '60px',
                maxHeight: '200px',
                overflowY: 'auto',
              }}>
                {formatValue(change.old)}
              </div>
              <span style={{ color: 'var(--color-text-muted)', paddingTop: 'var(--spacing-sm)' }}>→</span>
              <div style={{
                padding: 'var(--spacing-sm)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                minHeight: '60px',
                maxHeight: '200px',
                overflowY: 'auto',
              }}>
                {formatValue(change.new)}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderExtraFields = (extraFields: Record<string, ExtraFieldChangeDto>, showControls: boolean = false) => {
    const entries = Object.entries(extraFields)
    if (entries.length === 0) {
      return null
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {entries.map(([vendorKey, field]) => {
          const resolution = extraFieldResolutions[vendorKey]
          return (
            <div
              key={vendorKey}
              style={{
                padding: 'var(--spacing-sm) var(--spacing-md)',
                backgroundColor: 'var(--color-bg-tertiary)',
                borderRadius: 'var(--radius-sm)',
                borderLeft: '3px solid var(--color-warning)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-xs)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  <span style={{ fontWeight: 600, fontFamily: 'var(--font-family-mono)' }}>{vendorKey}</span>
                  <span className="badge" style={{ backgroundColor: 'var(--color-warning)', color: '#000', fontSize: 'var(--font-size-xs)' }}>
                    extra field
                  </span>
                </div>
              </div>
              <div style={{
                padding: 'var(--spacing-xs) var(--spacing-sm)',
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-family-mono)',
                fontSize: 'var(--font-size-sm)',
                wordBreak: 'break-word',
                marginBottom: showControls ? 'var(--spacing-sm)' : 0,
              }}>
                {field.value}
              </div>

              {showControls && (
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                    Action:
                  </label>
                  <select
                    value={resolution?.resolution || 'keep'}
                    onChange={(e) => handleExtraFieldResolution(vendorKey, e.target.value as 'keep' | 'discard' | 'map')}
                    style={{
                      padding: 'var(--spacing-xs) var(--spacing-sm)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--color-border)',
                      backgroundColor: 'var(--color-bg-primary)',
                      color: 'var(--color-text-primary)',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    <option value="keep">Keep in extra fields</option>
                    <option value="discard">Discard</option>
                    <option value="map">Map to field...</option>
                  </select>

                  {resolution?.resolution === 'map' && (
                    <select
                      value={resolution?.mappedTo || ''}
                      onChange={(e) => handleExtraFieldResolution(vendorKey, 'map', e.target.value)}
                      style={{
                        padding: 'var(--spacing-xs) var(--spacing-sm)',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--color-border)',
                        backgroundColor: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)',
                        fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      <option value="">Select target field...</option>
                      {MAPPABLE_FIELDS.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const formatSourceType = (sourceType: string, vendor?: string | null) => {
    if (sourceType === 'vendor_email' && vendor) {
      return `Vendor: ${vendor}`
    }
    if (sourceType === 'app_user') {
      return 'App User'
    }
    return sourceType
  }

  return (
    <div style={{ padding: 'var(--spacing-lg)', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <h2 style={{ margin: 0 }}>Pending Changes</h2>
        <p style={{ color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-xs)' }}>
          {isAdmin
            ? 'Review and approve changes from vendor updates. Accept, reject, or merge each change.'
            : 'View pending changes awaiting admin review.'}
        </p>
      </div>

      {error && (
        <div style={{
          padding: 'var(--spacing-md)',
          backgroundColor: 'var(--color-danger-bg, rgba(220, 38, 38, 0.1))',
          border: '1px solid var(--color-danger)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-lg)',
          color: 'var(--color-danger)',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
          Loading pending changes...
        </div>
      ) : versions.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 'var(--spacing-xl)',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--color-text-secondary)',
        }}>
          No pending changes awaiting review.
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 'var(--spacing-md)', color: 'var(--color-text-secondary)' }}>
            Showing {versions.length} of {totalCount} pending changes
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
            {versions.map((version) => {
              const isExpanded = expandedVersions.has(version.sampleId)
              const isReviewing = reviewingVersion === version.versionId
              const changeCount = Object.keys(version.changes).length
              const extraFieldCount = version.extraFieldChanges ? Object.keys(version.extraFieldChanges).length : 0

              return (
                <div
                  key={version.sampleId}
                  style={{
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--color-bg-secondary)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      padding: 'var(--spacing-md)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? 'var(--color-bg-tertiary)' : 'transparent',
                    }}
                    onClick={() => toggleExpand(version.sampleId)}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
                        <PendingDot />
                        <span style={{ fontWeight: 600 }}>{version.sampleTitle}</span>
                        {changeCount > 0 && (
                          <span className="badge" style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}>
                            {changeCount} field{changeCount !== 1 ? 's' : ''} changed
                          </span>
                        )}
                        {extraFieldCount > 0 && (
                          <span className="badge" style={{ backgroundColor: 'var(--color-info, #3b82f6)', color: '#fff' }}>
                            {extraFieldCount} extra field{extraFieldCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="badge" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                          {formatSourceType(version.sourceType, version.sourceVendor)}
                        </span>
                      </div>
                      <div style={{
                        display: 'flex',
                        gap: 'var(--spacing-lg)',
                        marginTop: 'var(--spacing-xs)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)',
                      }}>
                        {version.subjectName && <span>Subject: {version.subjectName}</span>}
                        <span>Version #{version.versionNumber}</span>
                        <span>Created: {new Date(version.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                      {isAdmin && (
                        <>
                          <button
                            className="btn btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation()
                              setModal({ type: 'review', version, action: 'reject' })
                            }}
                            disabled={isReviewing}
                            style={{ fontSize: 'var(--font-size-sm)' }}
                          >
                            Reject
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (version.extraFieldChanges) {
                                initExtraFieldResolutions(version.extraFieldChanges)
                              }
                              setModal({ type: 'review', version, action: 'accept' })
                            }}
                            disabled={isReviewing}
                            style={{ fontSize: 'var(--font-size-sm)' }}
                          >
                            {isReviewing ? 'Processing...' : 'Accept'}
                          </button>
                        </>
                      )}
                      <span style={{
                        color: 'var(--color-text-muted)',
                        fontSize: 'var(--font-size-lg)',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.2s',
                      }}>
                        v
                      </span>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div style={{ padding: 'var(--spacing-md)', borderTop: '1px solid var(--color-border)' }}>
                      {changeCount > 0 && (
                        <>
                          <h4 style={{ margin: '0 0 var(--spacing-md) 0' }}>Field Changes</h4>
                          {renderChanges(version.changes)}
                        </>
                      )}

                      {version.extraFieldChanges && extraFieldCount > 0 && (
                        <div style={{ marginTop: changeCount > 0 ? 'var(--spacing-lg)' : 0 }}>
                          <h4 style={{ margin: '0 0 var(--spacing-md) 0', display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                            Extra Vendor Fields
                            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                              (unmapped fields from vendor)
                            </span>
                          </h4>
                          {renderExtraFields(version.extraFieldChanges, false)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 'var(--spacing-sm)',
              marginTop: 'var(--spacing-lg)',
            }}>
              <button
                className="btn btn-secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <span style={{
                padding: 'var(--spacing-sm) var(--spacing-md)',
                color: 'var(--color-text-secondary)',
              }}>
                Page {page} of {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Review Confirmation Modal */}
      {modal.type === 'review' && (
        <ConfirmModal
          title={`${modal.action === 'accept' ? 'Accept' : 'Reject'} Changes`}
          confirmLabel={reviewingVersion ? 'Processing...' : modal.action === 'accept' ? 'Accept Changes' : 'Reject Changes'}
          onConfirm={() => handleReview(modal.version, modal.action)}
          onCancel={() => {
            setModal({ type: 'none' })
            setReviewNotes('')
          }}
          isLoading={!!reviewingVersion}
          confirmVariant={modal.action === 'reject' ? 'danger' : 'primary'}
        >
          <div style={{ padding: 'var(--spacing-md)', maxHeight: '70vh', overflowY: 'auto' }}>
            <p style={{ margin: '0 0 var(--spacing-md) 0' }}>
              {modal.action === 'accept'
                ? `Are you sure you want to accept these changes to "${modal.version.sampleTitle}"? The sample will be updated with the new values.`
                : `Are you sure you want to reject these changes to "${modal.version.sampleTitle}"? The proposed changes will be discarded.`}
            </p>

            {Object.keys(modal.version.changes).length > 0 && (
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <h4 style={{ margin: '0 0 var(--spacing-sm) 0' }}>Field Changes to {modal.action}:</h4>
                {renderChanges(modal.version.changes)}
              </div>
            )}

            {modal.version.extraFieldChanges && Object.keys(modal.version.extraFieldChanges).length > 0 && (
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <h4 style={{ margin: '0 0 var(--spacing-sm) 0', display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                  Extra Vendor Fields
                  {modal.action === 'accept' && (
                    <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 400, color: 'var(--color-text-secondary)' }}>
                      (choose what to do with each)
                    </span>
                  )}
                </h4>
                {renderExtraFields(modal.version.extraFieldChanges, modal.action === 'accept')}
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500 }}>
                Review Notes (optional)
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder="Add any notes about this review decision..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: 'var(--spacing-sm)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--color-border)',
                  backgroundColor: 'var(--color-bg-primary)',
                  color: 'var(--color-text-primary)',
                  resize: 'vertical',
                }}
              />
            </div>
          </div>
        </ConfirmModal>
      )}
    </div>
  )
}
